import { useLanguage } from "@/lib/i18n";
import { Link } from "react-router-dom";
import { ArrowRight, Award, Users, Wrench, Shield } from "lucide-react";
import SEO from "@/components/SEO";

export default function AboutPage() {
  const { t, isRTL } = useLanguage();

  return (
    <div data-testid="about-page">
      <SEO 
        title="About Pruva Jacuzzi - Premium Spa Manufacturer Dubai"
        description="Pruva Jacuzzi is a premium hydrotherapy spa line by Pruva Acrylic Swimming Pools LLC. Specialized in swimming pool engineering, acrylic panels, and luxury spa systems for Dubai villas and developments."
        keywords="about pruva jacuzzi, spa manufacturer dubai, jacuzzi supplier UAE, swimming pool contractor, acrylic spa pools, luxury spa dubai"
        canonical={typeof window !== 'undefined' ? `${window.location.origin}/about` : "https://www.pruvajacuzzi.com/about"}
        breadcrumbs={[
          { name: "Home", url: typeof window !== 'undefined' ? window.location.origin : "https://www.pruvajacuzzi.com" },
          { name: "About Us", url: typeof window !== 'undefined' ? `${window.location.origin}/about` : "https://www.pruvajacuzzi.com/about" }
        ]}
      />
      {/* Hero */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-20" style={{ background: "linear-gradient(135deg, #F8F9FA 0%, #F1F3F5 100%)" }}>
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <p className="text-sm uppercase tracking-widest text-amber-700 font-medium mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>
            {t("about.subtitle")}
          </p>
          <h1 data-testid="about-title" className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-slate-900">
            {t("about.title")}
          </h1>
        </div>
      </section>

      {/* Image */}
      <section className="py-12 md:py-16 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-5xl">
          <div className="overflow-hidden bg-slate-50 aspect-[16/9]">
            <img src="/images/optimized/about-hero.webp" alt="Pruva Jacuzzi balcony installations in Dubai high-rise"
              className="w-full h-auto object-cover object-center" loading="lazy" width="800" height="446" decoding="async" />
          </div>
        </div>
      </section>

      {/* About Content */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <div className="space-y-8">
            <div data-testid="about-intro">
              <h2 className="text-2xl md:text-3xl font-medium text-slate-900 mb-4">{t("about.whoWeAre")}</h2>
              <p className="text-lg md:text-xl text-slate-700 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                {t("about.whoWeAreText")}
              </p>
            </div>

            <div data-testid="about-experience">
              <h2 className="text-2xl md:text-3xl font-medium text-slate-900 mb-4">{t("about.ourExpertise")}</h2>
              <p className="text-base md:text-lg text-slate-600 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                {t("about.ourExpertiseText1")}
              </p>
            </div>

            <div data-testid="about-quality">
              <h2 className="text-2xl md:text-3xl font-medium text-slate-900 mb-4">{t("about.qualityCraftsmanship")}</h2>
              <p className="text-base md:text-lg text-slate-600 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                {t("about.qualityCraftsmanshipText")}
              </p>
            </div>

            <div data-testid="about-team">
              <h2 className="text-2xl md:text-3xl font-medium text-slate-900 mb-4">{t("about.ourTeam")}</h2>
              <p className="text-base md:text-lg text-slate-600 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                {t("about.ourTeamText")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <h2 className="text-2xl md:text-3xl font-medium text-slate-900 mb-12 text-center">{t("about.whyChoosePruva")}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center p-6">
              <Award className="w-10 h-10 text-amber-700 mx-auto mb-4" strokeWidth={1.5} />
              <h3 className="text-lg font-medium text-slate-900 mb-2">{t("about.premiumQuality")}</h3>
              <p className="text-sm text-slate-600">{t("about.premiumQualityDesc")}</p>
            </div>
            <div className="text-center p-6">
              <Users className="w-10 h-10 text-amber-700 mx-auto mb-4" strokeWidth={1.5} />
              <h3 className="text-lg font-medium text-slate-900 mb-2">{t("about.expertTeam")}</h3>
              <p className="text-sm text-slate-600">{t("about.expertTeamDesc")}</p>
            </div>
            <div className="text-center p-6">
              <Wrench className="w-10 h-10 text-amber-700 mx-auto mb-4" strokeWidth={1.5} />
              <h3 className="text-lg font-medium text-slate-900 mb-2">{t("about.fullService")}</h3>
              <p className="text-sm text-slate-600">{t("about.fullServiceDesc")}</p>
            </div>
            <div className="text-center p-6">
              <Shield className="w-10 h-10 text-amber-700 mx-auto mb-4" strokeWidth={1.5} />
              <h3 className="text-lg font-medium text-slate-900 mb-2">{t("about.warrantySupport")}</h3>
              <p className="text-sm text-slate-600">{t("about.warrantySupportDesc")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
          <h2 className="text-2xl md:text-3xl font-medium text-slate-900 mb-4">{t("about.readyToStart")}</h2>
          <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">{t("about.readyToStartDesc")}</p>
          <Link to="/contact" className={`inline-flex items-center gap-2 bg-slate-800 text-white px-8 py-4 text-sm uppercase tracking-widest font-medium hover:bg-slate-700 transition-colors duration-300 ${isRTL ? 'flex-row-reverse' : ''}`}>
            {t("about.contactUs")} <ArrowRight className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
          </Link>
        </div>
      </section>

      {/* Parent Company Section */}
      <section className="py-16 md:py-20 bg-gray-100">
        <div className="container mx-auto px-4 md:px-8 max-w-5xl">
          <div className={`flex flex-col md:flex-row items-center justify-between gap-8 md:gap-12 ${isRTL ? 'md:flex-row-reverse' : ''}`}>
            <div className={`flex-1 text-center ${isRTL ? 'md:text-right' : 'md:text-left'}`}>
              <p className="text-sm uppercase tracking-widest text-slate-500 font-medium mb-3">{t("about.partOf")}</p>
              <h2 className="text-2xl md:text-3xl font-medium text-slate-900 mb-4">{t("about.parentCompany")}</h2>
              <p className="text-slate-600 mb-6 max-w-lg">
                {t("about.parentCompanyDesc")}
              </p>
              <a 
                href="https://www.pruvapools.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className={`inline-flex items-center gap-2 bg-cyan-500 text-white px-6 py-3 text-sm uppercase tracking-widest font-medium hover:bg-cyan-600 transition-colors duration-300 ${isRTL ? 'flex-row-reverse' : ''}`}
              >
                {t("about.visitParent")} <ArrowRight className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
              </a>
            </div>
            <div className="flex-shrink-0">
              <a href="https://www.pruvapools.com" target="_blank" rel="noopener noreferrer">
                <img 
                  src="/images/optimized/pruva-pools-logo.webp" 
                  alt="Pruva Swimming Pools - Parent Company" 
                  className="w-48 md:w-64 h-auto"
                />
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
